/* ==========================================================================
   Dry Eye Rescue WHOLESALE - PDP description accordions and fact chips.

   Ported from retail's assets/der-pdp.js, 8 September 2026. The accordion
   logic is unchanged because it was already theme-agnostic; what changed is
   every selector that touches the theme, because wholesale runs Warehouse and
   retail does not:

     retail                        wholesale (Warehouse)
     .product-options              .product-form__variants
     variant-picker                (no custom element)
     .der-pdp-crumbs li            .breadcrumb__item
     .der-pdp-chips                .derw-pdp-chips

   What it does, and what it deliberately does not do:
   - The Cin7-synced product description is printed normally by Liquid, so it
     is in the HTML source for crawlers before this file runs. Nothing is
     fetched, lazy-loaded or duplicated.
   - It builds the accordion from a DETACHED CLONE first. The visible
     description is only replaced once the whole accordion has been built
     without throwing, so a parse failure leaves the full description on
     screen instead of a blank panel.
   - Content is moved, never copied: after the swap the description exists
     exactly once in the DOM.

   No dependencies. Safe to load on any page: every entry point returns early
   when its mount point is absent.
   ========================================================================== */
(function () {
  'use strict';

  var HEADING_TAGS = { H2: 1, H3: 1, H4: 1 };

  /* Wholesale descriptions come from two Cin7 pipelines and a decade of manual
     edits, so a good number arrive wrapped in a single container - an <article>
     or a ckeditor <div> - with the real h3 headings one level down. Retail's
     version scanned only the top level and sent every one of those to the
     single-panel fallback. Measured against all 860 active wholesale products,
     descending through a lone wrapper and looking through a lone inline wrapper
     for the bold run moves 19 products from one panel to real sections, with
     zero products moving the other way. */
  var WRAPPER_TAGS = { DIV: 1, ARTICLE: 1, SECTION: 1 };
  var INLINE_WRAPPERS = { SPAN: 1, EM: 1, I: 1, FONT: 1 };

  var uid = 0;

  function isBlank(node) {
    return node.nodeType === 3 && !/\S/.test(node.nodeValue);
  }

  function isRealHeading(node) {
    return node.nodeType === 1 && HEADING_TAGS[node.tagName] === 1;
  }

  /* A <p> counts as a heading only when the paragraph IS a single bold run,
     is short, does not end like a sentence, and is not the last node. This is
     a fallback: it is only consulted when the description has no h2/h3/h4 at
     all, so a bold sentence inside a normally structured description can
     never be mistaken for a section break. */
  function isBoldHeading(node, isLast) {
    if (node.nodeType !== 1 || node.tagName !== 'P' || isLast) return false;
    if (node.children.length !== 1) return false;
    /* Look through a lone <span>/<em> so <p><span><strong>X</strong></span></p>
       counts, which is how a lot of the older wholesale copy is marked up. */
    var inner = node.children[0];
    for (var d = 0; d < 3; d++) {
      if (INLINE_WRAPPERS[inner.tagName] === 1 && inner.children.length === 1) {
        inner = inner.children[0];
      } else {
        break;
      }
    }
    if (inner.tagName !== 'STRONG' && inner.tagName !== 'B') return false;
    var text = (node.textContent || '').trim();
    if (text !== (inner.textContent || '').trim()) return false;
    if (text.length < 2 || text.length > 70) return false;
    if (/[.,;]$/.test(text)) return false;
    return true;
  }

  /* Element and non-whitespace text children, in document order. */
  function meaningfulChildren(el) {
    var out = [];
    for (var i = 0; i < el.childNodes.length; i++) {
      if (!isBlank(el.childNodes[i])) out.push(el.childNodes[i]);
    }
    return out;
  }

  function meaningful(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.nodeType === 1) return true;
      if (n.nodeType === 3 && /\S/.test(n.nodeValue)) return true;
    }
    return false;
  }

  /* Split a flat list of nodes into [{heading|null, label, nodes[]}] */
  function group(nodes, test) {
    var groups = [];
    var current = null;
    var intro = [];
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (test(node, i === nodes.length - 1)) {
        current = { heading: node, label: (node.textContent || '').trim(), nodes: [] };
        groups.push(current);
      } else if (current) {
        current.nodes.push(node);
      } else {
        intro.push(node);
      }
    }
    return { intro: intro, groups: groups };
  }

  function buildItem(headingEl, labelText, bodyNodes, open, tagFallback) {
    uid += 1;
    var btnId = 'derw-acc-btn-' + uid;
    var panelId = 'derw-acc-panel-' + uid;

    var item = document.createElement('div');
    item.className = 'derw-acc__item';

    var head = headingEl;
    if (!head) {
      head = document.createElement(tagFallback || 'h3');
    }
    head.className = 'derw-acc__head';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'derw-acc__btn';
    btn.id = btnId;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn.setAttribute('aria-controls', panelId);

    var label = document.createElement('span');
    label.className = 'derw-acc__label';
    if (headingEl) {
      while (head.firstChild) label.appendChild(head.firstChild);
    } else {
      label.appendChild(document.createTextNode(labelText));
    }
    btn.appendChild(label);

    var icon = document.createElement('span');
    icon.className = 'derw-acc__icon';
    icon.setAttribute('aria-hidden', 'true');
    btn.appendChild(icon);

    head.appendChild(btn);

    var panel = document.createElement('div');
    panel.className = 'derw-acc__panel';
    panel.id = panelId;
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-labelledby', btnId);
    if (!open) panel.hidden = true;

    var body = document.createElement('div');
    body.className = 'derw-acc__body rte';
    for (var i = 0; i < bodyNodes.length; i++) body.appendChild(bodyNodes[i]);
    panel.appendChild(body);

    item.appendChild(head);
    item.appendChild(panel);
    return item;
  }

  function build(src, opts) {
    /* Work on a clone so the live description is untouched until we succeed. */
    var work = src.cloneNode(true);
    var nodes = meaningfulChildren(work);
    if (!nodes.length) return null;

    /* Descend through a lone container so headings nested one or two levels
       down are found. Only ever triggers where the whole description is a
       single wrapper, so a normally structured description is untouched. */
    for (var w = 0; w < 3; w++) {
      if (nodes.length === 1 && nodes[0].nodeType === 1 && WRAPPER_TAGS[nodes[0].tagName] === 1) {
        var inner = meaningfulChildren(nodes[0]);
        if (!inner.length) break;
        nodes = inner;
      } else {
        break;
      }
    }

    var res = group(nodes, isRealHeading);
    if (!res.groups.length) res = group(nodes, isBoldHeading);

    var acc = document.createElement('div');
    acc.className = 'derw-acc';

    if (!res.groups.length) {
      /* Mandatory fallback: one accordion holding the whole description. */
      acc.appendChild(buildItem(null, opts.fallbackLabel, nodes, true, 'h3'));
      acc.setAttribute('data-derw-acc-mode', 'fallback');
    } else {
      var index = 0;
      if (meaningful(res.intro)) {
        acc.appendChild(buildItem(null, opts.overviewLabel, res.intro, true, 'h3'));
        index = 1;
      }
      for (var g = 0; g < res.groups.length; g++) {
        var grp = res.groups[g];
        if (!grp.label) continue;
        acc.appendChild(buildItem(grp.heading, grp.label, grp.nodes, index === 0, null));
        index += 1;
      }
      acc.setAttribute('data-derw-acc-mode', 'parsed');
    }

    if (!acc.children.length) return null;
    return acc;
  }

  function wire(acc) {
    var btns = acc.querySelectorAll('.derw-acc__btn');

    acc.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('.derw-acc__btn') : null;
      if (!btn || !acc.contains(btn)) return;
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
    });

    /* Arrow / Home / End move between headers. Enter and Space are native. */
    acc.addEventListener('keydown', function (e) {
      var btn = e.target.closest ? e.target.closest('.derw-acc__btn') : null;
      if (!btn) return;
      var list = Array.prototype.slice.call(btns);
      var i = list.indexOf(btn);
      if (i === -1) return;
      var next = null;
      if (e.key === 'ArrowDown') next = list[(i + 1) % list.length];
      else if (e.key === 'ArrowUp') next = list[(i - 1 + list.length) % list.length];
      else if (e.key === 'Home') next = list[0];
      else if (e.key === 'End') next = list[list.length - 1];
      if (next) { e.preventDefault(); next.focus(); }
    });
  }

  function initAccordion() {
    var wrap = document.querySelector('[data-derw-pdp-acc]');
    if (!wrap) return;
    var src = wrap.querySelector('[data-derw-pdp-src]');
    if (!src) return;
    if (src.getAttribute('data-derw-acc-done') === '1') return;

    var acc;
    try {
      acc = build(src, {
        overviewLabel: wrap.getAttribute('data-overview-label') || 'Product Overview',
        fallbackLabel: wrap.getAttribute('data-fallback-label') || 'Product Information'
      });
    } catch (err) {
      if (window.console && console.warn) console.warn('[derw-pdp] accordion skipped, showing full description:', err);
      return;
    }
    if (!acc) return;

    /* Commit: only now is the raw description replaced. */
    try {
      while (src.firstChild) src.removeChild(src.firstChild);
      src.appendChild(acc);
      src.setAttribute('data-derw-acc-done', '1');
      wrap.classList.add('derw-pdp-desc--acc');
      wire(acc);
    } catch (err2) {
      if (window.console && console.warn) console.warn('[derw-pdp] accordion commit failed:', err2);
    }
  }

  /* ----------------------------------------------------------------------
     Variant chip.

     The chip is printed by Liquid from the variant selected at page load.
     Switching variants on the page is client side - Warehouse swaps the price
     and the hidden id without a reload - so without this the chip would keep
     showing the variant the buyer arrived on. It reads the CHECKED INPUTS
     rather than any theme event payload, so it survives a theme update that
     changes how variant changes are announced.

     On wholesale the variant selector only renders for a logged-in customer,
     so every function here has to cope with the picker being absent.
     ---------------------------------------------------------------------- */
  function variantScope() {
    return document.querySelector('.product-form__variants');
  }

  function variantLabel() {
    var scope = variantScope();
    if (!scope) return '';
    var parts = [];
    var checked = scope.querySelectorAll('input.product-form__single-selector:checked');
    if (checked.length) {
      for (var i = 0; i < checked.length; i++) parts.push(checked[i].value);
    } else {
      var sels = scope.querySelectorAll('select.product-form__single-selector');
      for (var j = 0; j < sels.length; j++) if (sels[j].value) parts.push(sels[j].value);
    }
    return parts.join(' / ').replace(/\s+/g, ' ').trim();
  }

  function syncVariantChip() {
    var chip = document.querySelector('[data-derw-variant-chip]');
    if (!chip) return;
    var label = variantLabel();
    if (!label || /^default title$/i.test(label)) {
      chip.hidden = true;
      return;
    }
    chip.hidden = false;
    chip.textContent = label;
  }

  /* ----------------------------------------------------------------------
     Size and supply chips.

     Both are read from the selected VARIANT's metafields, so each pack size
     of a multi-variant product carries its own values - ZEISS Lens Wipes at
     30 / 60 / 120 / 180 ct no longer share one number. Liquid renders the
     variant the buyer lands on; switching happens without a reload, so every
     variant's values are printed once and swapped in here.

     The data arrives as id|size|supply records separated by ~, not JSON:
     Liquid cannot emit a literal closing brace next to its own delimiter and
     Shopify rejects the file if you try.
     ---------------------------------------------------------------------- */
  var factsCache;
  function variantFacts() {
    if (factsCache !== undefined) return factsCache;
    var el = document.querySelector('[data-derw-variant-facts]');
    if (!el) { factsCache = null; return factsCache; }
    var map = {};
    var records = (el.textContent || '').split('~');
    for (var i = 0; i < records.length; i++) {
      var parts = records[i].split('|');
      if (parts.length < 3) continue;
      var id = parts[0].trim();
      if (id) map[id] = { size: parts[1].trim(), supply: parts[2].trim() };
    }
    factsCache = map;
    return factsCache;
  }

  function setChip(selector, value) {
    var el = document.querySelector(selector);
    if (!el) return;
    if (!value) { el.hidden = true; return; }
    el.textContent = value;
    el.hidden = false;
  }

  function syncFactChips() {
    var map = variantFacts();
    if (!map) return;
    var id = selectedVariantId(priceFacts());
    var f = id && map[id];
    if (!f) return;
    setChip('[data-derw-size-chip]', f.size);
    setChip('[data-derw-supply-chip]', f.supply);
  }

  /* ----------------------------------------------------------------------
     The breadcrumb and the chip row are rendered separately, so neither can
     see what the other printed. On products where the breadcrumb falls back
     to the Cin7 sub category, the same words appear twice on one screen.

     Comparing the rendered text is exact and needs no duplicated Liquid, so
     the chip is dropped when it repeats the breadcrumb category. Chips that
     add something the crumb does not are left alone.
     ---------------------------------------------------------------------- */
  function dedupeChips() {
    var crumbs = document.querySelectorAll('.derw-pdp-crumbs .breadcrumb__item');
    if (crumbs.length < 3) return;               /* Home + category + product */
    var category = (crumbs[1].textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!category) return;
    var chips = document.querySelectorAll('.derw-pdp-chips li');
    for (var i = 0; i < chips.length; i++) {
      if (chips[i].hasAttribute('data-derw-variant-chip')) continue;
      if (chips[i].hasAttribute('data-derw-ndc-chip')) continue;
      var t = (chips[i].textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (t === category) chips[i].hidden = true;
    }
    var list = document.querySelector('.derw-pdp-chips');
    if (list && !list.querySelector('li:not([hidden])')) list.hidden = true;
  }

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('.product-form__variants')) {
      /* after Warehouse has finished updating the form */
      /* Twice: immediately, and again after the theme has finished its own
         update. Warehouse rewrites the hidden id input asynchronously, and on
         a slow render the first pass can read the old value. */
      var again = function () {
        syncVariantChip();
        syncFactChips();
        dedupeChips();
        syncPrices();
        renderQP();
      };
      setTimeout(again, 0);
      setTimeout(again, 300);
    }
  }, true);

  /* ----------------------------------------------------------------------
     Price trio and volume table.

     Both are driven from one payload of every variant's price and compare-at
     in cents, printed once by snippets/product-derw-price.liquid. Reading our
     own data rather than the theme's `variant:changed` payload means a
     Warehouse update that renames that event cannot silently freeze the
     numbers on a page that decides margin.

     Tiers mirror the live SupaEasy function: 10+ 5%, 20+ 10%, 100+ 15%,
     200+ 20%. Nothing here grants a discount; it only describes one.
     ---------------------------------------------------------------------- */
  /* ----------------------------------------------------------------------
     Resolving the selected variant.

     THE BUG THIS EXISTS TO FIX, and it is worth stating exactly, because the
     first push shipped with it. snippets/product-variant-selector.liquid:

         {%- unless product.has_only_default_variant -%}
           <div class="product-form__variants"> ... <select name="id"> </div>
         {%- else -%}
           <input type="hidden" name="id" value="...">
         {%- endunless -%}

     A SINGLE-VARIANT product gets a hidden INPUT. A MULTI-VARIANT product
     gets no input at all - the form field is a SELECT. The first version
     looked only for input[name="id"], so the volume table filled correctly on
     single-variant products and stayed empty on every multi-variant one.
     Aaron spotted the pattern from the live page.

     There are other input[name="id"] elements on the page too - every
     quick-buy card in product-item.liquid has one - so the lookup is scoped
     to the form containing our own markup rather than the document.

     Three ways in, in order of trust:
       1. the id field, input OR select, inside the SAME form as our markup
       2. the variant whose title matches the checked option inputs
       3. the id Liquid printed on the trio at page load
     ---------------------------------------------------------------------- */
  function derwForm() {
    var anchor = document.querySelector('[data-derw-price-facts]') ||
                 document.querySelector('[data-derw-variant-facts]') ||
                 document.querySelector('[data-derw-price]');
    return anchor && anchor.closest ? anchor.closest('form') : null;
  }

  function selectedVariantId(map) {
    var form = derwForm();
    var input = form ? form.querySelector('select[name="id"], input[name="id"]') : null;
    if (input && input.value && (!map || map[input.value])) return input.value;

    if (map) {
      var label = variantLabel();
      if (label) {
        var want = label.replace(/\s+/g, ' ').trim().toLowerCase();
        for (var id in map) {
          if (!Object.prototype.hasOwnProperty.call(map, id)) continue;
          var title = (map[id].title || '').replace(/\s+/g, ' ').trim().toLowerCase();
          if (title && title === want) return id;
        }
      }
    }

    var el = document.querySelector('[data-derw-selected]');
    var fallback = el ? el.getAttribute('data-derw-selected') : null;
    if (fallback && (!map || map[fallback])) return fallback;
    return input && input.value ? input.value : fallback;
  }

  /* Tier configuration is NOT owned here. snippets/product-derw-volume.liquid
     prints it onto the wrapper, so the table and this script can never drift
     apart. TIERS is only the fallback for a page that predates those data
     attributes. */
  var TIERS = [0, 5, 10, 15, 20];
  var MINS  = [1, 10, 20, 100, 200];

  function volEl() {
    return document.querySelector('[data-derw-vol]');
  }

  /* The element carrying the CONFIG, which is not always the element carrying
     the table. When a product is excluded from volume pricing - every RX and
     Medical Supplies SKU while the promotion runs - product-derw-volume.liquid
     renders .derw-vol--excluded instead of [data-derw-vol], and before 8 Sept
     that meant excludedTags() returned [] on exactly those pages and
     readEligibleCart() counted EVERY cart line as eligible. The excluded branch
     now publishes the same three attributes, and everything that reads config
     reads it from here. */
  function volCfgEl() {
    return document.querySelector('[data-derw-vol], [data-derw-excluded-tags]');
  }

  function numList(el, attr, fallback) {
    var raw = el && el.getAttribute(attr);
    if (!raw) return fallback.slice();
    var out = [];
    var parts = raw.split(',');
    for (var i = 0; i < parts.length; i++) {
      var n = parseInt(parts[i], 10);
      if (!isNaN(n)) out.push(n);
    }
    return out.length ? out : fallback.slice();
  }

  function tierPcts() { return numList(volCfgEl(), 'data-derw-pcts', TIERS); }
  function tierMins() { return numList(volCfgEl(), 'data-derw-mins', MINS); }

  /* Index of the tier a projected quantity lands in. */
  function tierIndexFor(qty, mins) {
    var idx = 0;
    for (var i = 0; i < mins.length; i++) if (qty >= mins[i]) idx = i;
    return idx;
  }

  var priceCache;

  function priceFacts() {
    if (priceCache !== undefined) return priceCache;
    var el = document.querySelector('[data-derw-price-facts]');
    if (!el) { priceCache = null; return priceCache; }
    var map = {};
    var records = (el.textContent || '').split('~');
    for (var i = 0; i < records.length; i++) {
      var parts = records[i].split('|');
      if (parts.length < 3) continue;
      var id = parts[0].trim();
      if (!id) continue;
      map[id] = {
        price: parseInt(parts[1], 10) || 0,
        compare: parseInt(parts[2], 10) || 0,
        title: (parts[3] || '').trim()
      };
    }
    priceCache = map;
    return priceCache;
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function setText(sel, value) {
    var el = document.querySelector(sel);
    if (el) el.textContent = value;
  }

  function show(sel, on) {
    var els = document.querySelectorAll(sel);
    for (var i = 0; i < els.length; i++) els[i].hidden = !on;
  }

  function syncPrices() {
    var map = priceFacts();
    if (!map) return;
    var id = selectedVariantId(map);
    var f = id && map[id];
    if (!f) return;

    var hasPatient = f.compare > f.price;

    setText('[data-derw-your]', money(f.price));
    show('[data-derw-patient-cell]', hasPatient);
    show('[data-derw-profit-cell]', hasPatient);
    if (hasPatient) {
      setText('[data-derw-patient]', money(f.compare));
      setText('[data-derw-profit]', money(f.compare - f.price));
    }

    /* Volume table.

       FLOOR, NOT ROUND, and this matters. $13.50 at 5% is 1282.5 cents. Round
       gives $12.83; floor gives $12.82, which is what the table on the store
       has always shown and what the mockup was drawn from. Overstating the
       price by a cent would also overstate the profit beside it, which is the
       number a practice is deciding on.

       Only the two half-cent tiers differ (5% and 15% on this product), and
       nobody has confirmed how the SupaEasy function itself rounds at
       checkout. Worth checking one real cart at a tier boundary. Until then
       this matches what the store already displayed. */
    if (!volEl()) return;
    show('[data-derw-vol-profit-head]', hasPatient);
    var pcts = tierPcts();
    for (var t = 0; t < pcts.length; t++) {
      var pct = pcts[t];
      var tierCents = Math.floor(f.price * (100 - pct) / 100);
      setText('[data-derw-vol-price="' + pct + '"]', money(tierCents));
      var cell = document.querySelector('[data-derw-vol-profit="' + pct + '"]');
      if (!cell) continue;
      cell.hidden = !hasPatient;
      cell.textContent = hasPatient ? money(f.compare - tierCents) : '';
    }
  }

  /* ======================================================================
     INTERACTIVE VOLUME PRICING PREVIEW

     Answers one question without arithmetic: "if I add this many to what is
     already in my cart, what will I pay?"

     IT IS A PREVIEW, AND ONLY A PREVIEW. Shopify's discount function decides
     what is actually charged. Everything here reads the same tier
     configuration the table reads - printed onto the wrapper by
     product-derw-volume.liquid - and computes the same arithmetic. If the two
     ever disagree, the cart is right and this is wrong.

     ELIGIBILITY. The function excludes products carrying certain tags, and
     /cart.js does not return tags. So the cart's line items are resolved to
     their products through /products/<handle>.js, which does, and the results
     are cached for the page. A cart bigger than the lookup cap, or any failed
     request, falls back to "we could not read your cart" rather than to a
     confident wrong number.

     ONE QUANTITY. The slider and the theme's own stepper are two views of the
     same value. Moving either moves the other, and Add to Cart adds exactly
     what is shown. There is no separate preview quantity.
     ====================================================================== */
  var QP_MAX_LOOKUPS = 40;
  var tagCache = {};
  var cartState = null;          /* null = not read yet, false = failed */
  var cartPending = false;

  /* Has the buyer engaged with the builder on THIS page view?
     False on every fresh load. See the note in renderQP for why the stepper's
     default 1 must not count as a queued add until they have. */
  var qpTouched = false;

  function qpEl() { return document.querySelector('[data-derw-qp]'); }

  function qtyInput() {
    var form = derwForm();
    return form ? form.querySelector('input[name="quantity"]:not([type="hidden"])') : null;
  }

  function excludedTags() {
    var el = volCfgEl();
    var raw = el && el.getAttribute('data-derw-excluded-tags');
    if (!raw) return [];
    var out = [];
    var parts = raw.split(',');
    for (var i = 0; i < parts.length; i++) {
      var s = parts[i].trim().toLowerCase();
      if (s) out.push(s);
    }
    return out;
  }

  function getJSON(url) {
    return fetch(url, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error(url + ' -> ' + r.status);
        return r.json();
      });
  }

  /* Sum the cart quantities of every line whose product carries none of the
     excluded tags, and the money those lines are worth.

     THE MONEY IS NEW, 17 September 2026. The Cart Builder now says what the
     next tier is worth in dollars, and the only honest base for that is what
     /cart.js actually returned. original_line_price is this account's own
     contract price times the quantity, BEFORE any discount, so the figure
     reflects the buyer's catalog rather than list. line_price is deliberately
     not used: an order-class allocation already taken off it would be counted
     twice.

     resolved is false whenever ANY line's tags could not be read. The unit
     count already degrades gracefully in that case and says so; a dollar
     figure cannot, so it is suppressed rather than guessed. */
  function readEligibleCart() {
    if (cartPending) return;
    cartPending = true;
    var blocked = excludedTags();

    getJSON('/cart.js').then(function (cart) {
      var items = (cart && cart.items) || [];
      var handles = [];
      for (var i = 0; i < items.length; i++) {
        var h = items[i].handle;
        if (h && handles.indexOf(h) === -1) handles.push(h);
      }
      if (handles.length > QP_MAX_LOOKUPS) throw new Error('cart too large to resolve');

      var needed = [];
      for (var j = 0; j < handles.length; j++) {
        if (!Object.prototype.hasOwnProperty.call(tagCache, handles[j])) needed.push(handles[j]);
      }

      return Promise.all(needed.map(function (h) {
        return getJSON('/products/' + encodeURIComponent(h) + '.js')
          .then(function (p) { tagCache[h] = (p && p.tags) || []; })
          .catch(function () { tagCache[h] = null; });   /* unknown, not empty */
      })).then(function () {
        var total = 0, unknown = 0, cents = 0;
        for (var k = 0; k < items.length; k++) {
          var tags = tagCache[items[k].handle];
          if (tags === null || tags === undefined) { unknown += 1; continue; }
          var isBlocked = false;
          for (var m = 0; m < tags.length; m++) {
            if (blocked.indexOf(String(tags[m]).trim().toLowerCase()) !== -1) { isBlocked = true; break; }
          }
          if (!isBlocked) {
            total += items[k].quantity;
            var lineCents = items[k].original_line_price;
            if (typeof lineCents !== 'number') lineCents = items[k].line_price;
            if (typeof lineCents === 'number') cents += lineCents;
            else unknown += 1;
          }
        }
        cartState = { qty: total, cents: cents, unknown: unknown, resolved: unknown === 0 };
        cartPending = false;
        renderQP();
        renderRx();
      });
    }).catch(function () {
      cartState = false;
      cartPending = false;
      renderQP();
      renderRx();
    });
  }

  /* ---- The slider scale --------------------------------------------------
     REBASED 17 September 2026. The track used to carry the quantity of THIS
     product. It now carries the TOTAL eligible units in the cart, which is
     what the SupaEasy function actually counts (its basis is :cart_items).

     The consequence is the whole point of the change: the 10 / 20 / 100 / 200
     markers sit at their real thresholds and DO NOT MOVE. Before, a cart of
     16 drew the "20" marker four units along the track, which was arithmetic
     the buyer should never have had to reverse-engineer from a moving label.

     The domain runs from lo = cart + 1 (you have to add at least one unit to
     be on this page at all) to hi = cart + addCap, where addCap is the
     per-page ceiling on data-derw-qp-max. On an empty cart that is 1 to 200,
     identical to what shipped in September.

     LOGARITHMIC, unchanged in kind. Equal travel is an equal percentage
     change, so the scale is fine where the numbers are small and coarse where
     they are large. On an empty cart and this tier set:

         1 -> 10    43.5% of the track
        10 -> 20    13.0%
        20 -> 100   30.4%
       100 -> 200   13.1%

     A full cart compresses the ratio hi/lo and the scale tends toward linear
     on its own, which is the right behaviour: at 180 units in the cart nobody
     is fine-tuning the difference between 187 and 188.

     The range input therefore carries POSITION units (0-1000). Everything
     outside these three functions speaks in cart totals or in add quantities,
     never in positions, and the conversion round-trips exactly for every
     integer total in the domain. */
  function qpAddCap() {
    var qp = qpEl();
    var range = qp && qp.querySelector('[data-derw-qp-range]');
    var cap = range ? parseInt(range.getAttribute('data-derw-qp-max'), 10) : 200;
    if (!cap || isNaN(cap) || cap < 2) cap = 200;
    return cap;
  }

  /* Eligible units already in the cart, or 0 while that is unknown. The
     unknown case is not an error here: it just means the track starts at 1
     and the status line above it says the count could not be read. */
  function cartUnits() {
    return (cartState && cartState !== true && typeof cartState.qty === 'number') ? cartState.qty : 0;
  }

  function qpBounds() {
    var have = cartUnits();
    var cap = qpAddCap();
    return { lo: have + 1, hi: have + cap };
  }

  function posToTotal(pos) {
    var b = qpBounds();
    pos = Math.max(0, Math.min(parseInt(pos, 10) || 0, 1000));
    var t = Math.exp((pos / 1000) * Math.log(b.hi / b.lo)) * b.lo;
    return Math.max(b.lo, Math.min(Math.round(t), b.hi));
  }

  function totalToPos(total) {
    var b = qpBounds();
    total = parseInt(total, 10) || b.lo;
    if (total <= b.lo) return 0;
    if (total >= b.hi) return 1000;
    return Math.round(Math.log(total / b.lo) / Math.log(b.hi / b.lo) * 1000);
  }

  /* n is the quantity of THIS product being added, which is what the theme's
     own stepper and the add-to-cart form have always carried. The slider
     position is derived from it plus the cart, not the other way round, so
     the two views cannot disagree. */
  function setQty(n, fromSlider) {
    var input = qtyInput();
    var range = document.querySelector('[data-derw-qp-range]');
    n = Math.max(1, Math.min(parseInt(n, 10) || 1, 9999));
    if (input && parseInt(input.value, 10) !== n) {
      input.value = n;
      /* Only announce the change when WE changed it. Echoing an event back at
         the input that just fired one is how sync loops start. */
      if (fromSlider) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (range) {
      var want = totalToPos(cartUnits() + n);
      if (parseInt(range.value, 10) !== want) range.value = want;
    }
  }

  /* Set the CART TOTAL. The ladder rungs and the next-tier button both come
     through here, because a threshold is a total and never an add quantity.
     Clamped into the track's own domain so a rung the cart has already passed
     cannot ask for a negative add. */
  function setTotal(total, fromSlider) {
    var b = qpBounds();
    total = Math.max(b.lo, Math.min(parseInt(total, 10) || b.lo, b.hi));
    setQty(total - cartUnits(), fromSlider !== false);
  }

  function currentQty() {
    var input = qtyInput();
    var n = input ? parseInt(input.value, 10) : 1;
    if (isNaN(n) || n < 1) n = 1;
    return Math.min(n, qpAddCap());
  }

  /* Markers, on the CART TOTAL axis.

     A tier marker is drawn at its own threshold, full stop. No subtraction,
     no movement between page loads. A tier the cart has already passed is
     behind the left edge of the track and simply is not in the domain, which
     is honest: there is nothing left to reach.

     The left-hand marker is the cart's current position, drawn only when it
     is not already a tier threshold, so the track reads "you are here" at one
     end and "the cap" at the other. */
  function buildTicks(mins, b) {
    var wrap = document.querySelector('[data-derw-qp-ticks]');
    if (!wrap) return;
    wrap.innerHTML = '';

    var stops = [];
    var i;
    for (i = 0; i < mins.length; i++) {
      if (mins[i] >= b.lo && mins[i] <= b.hi) stops.push({ v: mins[i], tier: true, last: i === mins.length - 1 });
    }
    /* The "you are here" marker is dropped when the first tier threshold is
       close enough to collide with it. Measured on a 470px column: two labels
       need about 6% of the track between them, and a cart of 16 puts 17 and
       20 five points apart. A number sitting on top of another number is
       worse than no number. */
    if (!stops.length) {
      stops.unshift({ v: b.lo, tier: false, last: false });
    } else if (stops[0].v !== b.lo && totalToPos(stops[0].v) > 70) {
      stops.unshift({ v: b.lo, tier: false, last: false });
    }

    for (i = 0; i < stops.length; i++) {
      var s = stops[i];
      var tick = document.createElement('span');
      tick.className = 'derw-qp__tick' +
                       (i === 0 ? ' is-first' : '') +
                       (i === stops.length - 1 ? ' is-last' : '') +
                       (s.tier ? '' : ' derw-qp__tick--here');
      tick.style.left = (totalToPos(s.v) / 10).toFixed(3) + '%';
      tick.setAttribute('data-derw-qp-tick', String(s.v));
      tick.textContent = s.last ? s.v + '+' : String(s.v);
      wrap.appendChild(tick);
    }
  }

  /* ---- The tier ladder ---------------------------------------------------
     Added 17 September 2026, and it is the component the request was actually
     about: "so that they know how many they need to purchase to unlock the
     next savings discount".

     Built from data-derw-mins and data-derw-pcts, which product-derw-volume
     .liquid prints from the same two Liquid lists that render the reference
     table. Nothing here names 10, 20, 100, 200, 5, 10, 15 or 20. That matters
     because the 20% top tier is already hard-coded in four places on this
     store and this is not going to be the fifth.

     The DOM is built once and only its state is updated afterwards, so a
     keyboard user who has tabbed onto a rung does not lose focus every time
     the cart or the variant changes. The signature attribute is what detects
     a genuine config change. */
  function buildLadder(mins, pcts) {
    var wrap = document.querySelector('[data-derw-qp-ladder]');
    if (!wrap) return null;
    var sig = mins.join(',') + '|' + pcts.join(',');
    if (wrap.getAttribute('data-derw-qp-ladder-sig') === sig) return wrap;
    wrap.setAttribute('data-derw-qp-ladder-sig', sig);
    wrap.innerHTML = '';
    for (var i = 0; i < mins.length; i++) {
      if (!pcts[i]) continue;                       /* index 0 is the no-discount base */
      var rung = document.createElement('button');
      rung.type = 'button';
      rung.className = 'derw-qp__rung';
      rung.setAttribute('data-derw-qp-rung', String(i));
      rung.innerHTML = '<span class="derw-qp__rung-pct"></span><span class="derw-qp__rung-at"></span>';
      rung.querySelector('.derw-qp__rung-pct').textContent = pcts[i] + '%';
      rung.querySelector('.derw-qp__rung-at').textContent = mins[i] + '+ units';
      rung.setAttribute('aria-label', 'Set cart total to ' + mins[i] + ' units for ' + pcts[i] + ' percent off');
      wrap.appendChild(rung);
    }
    return wrap;
  }

  function renderLadder(mins, pcts, idx, projected, b) {
    var wrap = buildLadder(mins, pcts);
    if (!wrap) return;
    var rungs = wrap.querySelectorAll('[data-derw-qp-rung]');
    for (var r = 0; r < rungs.length; r++) {
      var i = parseInt(rungs[r].getAttribute('data-derw-qp-rung'), 10);
      var min = mins[i];
      var reached = projected >= min;
      /* Out of reach means out of reach ON THIS PAGE: the threshold sits above
         what the add cap can carry the cart to. The rung stays visible,
         because a ladder missing its top rung is not a ladder, but it cannot
         be pressed into a promise the slider could not keep. */
      var reachable = min >= b.lo && min <= b.hi;
      rungs[r].classList.toggle('is-reached', reached);
      rungs[r].classList.toggle('is-current', i === idx);
      rungs[r].classList.toggle('is-next', i === idx + 1);
      rungs[r].disabled = !reachable;
    }
  }

  /* The status strip under the Cart Builder is one line on a desktop column
     and wraps to two on a phone - measured at 50px against a 34-38px target.
     The sentence is what does not fit, not the padding, so the phone gets a
     shorter one. Desktop keeps the fuller wording.

     Read at render time rather than cached, and re-rendered when the
     breakpoint changes, so rotating a phone does not leave the long sentence
     behind. */
  var QP_MQ_NARROW = '(max-width: 700px)';
  var QP_MQ_TINY   = '(max-width: 345px)';

  function qpWidthTier() {
    if (!window.matchMedia) return 0;
    if (window.matchMedia(QP_MQ_TINY).matches) return 2;
    if (window.matchMedia(QP_MQ_NARROW).matches) return 1;
    return 0;
  }

  function renderQP() {
    var qp = qpEl();
    if (!qp) return;
    var map = priceFacts();
    if (!map) return;
    var id = selectedVariantId(map);
    var f = id && map[id];
    if (!f) return;

    qp.hidden = false;

    var pcts = tierPcts();
    var mins = tierMins();
    var b = qpBounds();
    var qty = currentQty();

    var known = cartState && cartState !== true && typeof cartState.qty === 'number';
    var cartQty = cartUnits();

    /* PENDING is what this page would add, and it is ZERO until the buyer
       actually engages with the builder.

       The stepper sits at 1 on every fresh product page. Folding that 1 into
       the cart total made the module contradict itself in the same breath: it
       printed "Cart total  1 unit" directly above "No eligible units in your
       cart yet."

       It also made the call to action disagree with its own button. "need" was
       measured from cart + stepper, so an empty cart read "Add 9" - but the
       button carries the THRESHOLD in data-derw-qp-jump-to (10), and setTotal
       turns that into setQty(10 - cart) = 10. The label said 9 and the click
       set 10. The same gap existed on a loaded cart: at 16 units it read
       "Add 3" and set 4.

       Baselining on the real cart until engagement fixes both at once. Once
       the buyer moves the slider, types a quantity, or takes a rung, the
       projection is exactly what they asked to see, and pending follows the
       stepper again.

       The cart total already includes whatever is in there; the slider is the
       total AFTER this add, so the two are never summed twice. */
    var pending = qpTouched ? qty : 0;
    var projected = cartQty + pending;
    var idx = tierIndexFor(projected, mins);
    var pct = pcts[idx] || 0;
    var unit = Math.floor(f.price * (100 - pct) / 100);
    var hasPatient = f.compare > f.price;

    setText('[data-derw-qp-qty]', projected + (projected === 1 ? ' unit' : ' units'));

    var cartLine = qp.querySelector('[data-derw-qp-cart]');
    if (cartLine) {
      if (cartState === null) cartLine.textContent = 'Checking your cart…';
      else if (cartState === false) cartLine.textContent = 'We could not read your cart, so this counts this product alone.';
      else if (cartQty === 0) cartLine.textContent = 'No eligible units in your cart yet.';
      else cartLine.textContent = cartQty + ' eligible ' + (cartQty === 1 ? 'unit' : 'units') + ' already in your cart.';
    }

    /* Never claim an add that has not been queued. With nothing pending this
       line describes the cart alone, and disappears entirely when the cart is
       empty, rather than reading "1 unit added here" on a page the buyer has
       not touched. */
    var sumEl = qp.querySelector('[data-derw-qp-sum]');
    if (sumEl) {
      var sumText = pending === 0
        ? (known && cartQty > 0 ? cartQty + ' already in cart' : '')
        : (known && cartQty > 0
            ? cartQty + ' already in cart + ' + pending + ' added here'
            : pending + (pending === 1 ? ' unit' : ' units') + ' added here');
      sumEl.textContent = sumText;
      sumEl.hidden = (sumText === '');
    }

    setText('[data-derw-qp-projected]', qty + (qty === 1 ? ' unit' : ' units'));
    setText('[data-derw-qp-discount]', pct ? pct + '%' : 'None yet');
    setText('[data-derw-qp-price]', money(unit) + ' each');
    show('[data-derw-qp-profit-cell]', hasPatient);
    if (hasPatient) setText('[data-derw-qp-profit]', money(f.compare - unit) + ' each');

    renderLadder(mins, pcts, idx, projected, b);

    /* Next tier. It sits in its own band outside the module, because it is a
       call to action rather than another read-out, and since 17 September it
       carries the action itself. */
    var next = document.querySelector('[data-derw-qp-next]');
    var nextText = document.querySelector('[data-derw-qp-next-text]');
    var jump = document.querySelector('[data-derw-qp-jump]');
    var save = document.querySelector('[data-derw-qp-save]');
    if (next && nextText) {
      if (idx >= pcts.length - 1) {
        nextText.textContent = 'You have unlocked the highest ' + pct + '% volume tier.';
        next.classList.add('derw-qp__next--top');
        if (jump) jump.hidden = true;
        if (save) save.hidden = true;
      } else {
        var nextMin = mins[idx + 1];
        var nextPct = pcts[idx + 1];
        var need = nextMin - projected;
        /* Measured at 13px: the full sentence is one line on a desktop column
           and two on a phone. The middle wording fits one line down to 360px;
           at 320px only the shortest does. Each tier is the longest sentence
           that still fits on one line at that width. */
        var unitWord = need === 1 ? 'unit' : 'units';
        var tier = qpWidthTier();
        if (tier === 2) {
          nextText.textContent = need + ' more ' + unitWord + ' to unlock ' + nextPct + '%.';
        } else if (tier === 1) {
          nextText.textContent = need + ' more ' + unitWord + ' in the cart unlocks ' + nextPct + '%.';
        } else {
          nextText.textContent = need + ' more ' + unitWord +
                                 ' in your cart unlocks ' + nextPct + '% pricing.';
        }
        next.classList.remove('derw-qp__next--top');

        /* The button. It sets the CART TOTAL to the threshold, so the number
           printed on it is the extra units this page would add, which is the
           number a buyer would otherwise work out by hand. Hidden when the
           add cap cannot carry the cart that far, rather than shown as a
           button that silently clamps to something else. */
        if (jump) {
          if (nextMin >= b.lo && nextMin <= b.hi) {
            jump.textContent = 'Add ' + need;
            jump.setAttribute('data-derw-qp-jump-to', String(nextMin));
            jump.setAttribute('aria-label', 'Add ' + need + ' more ' + unitWord + ' to unlock ' + nextPct + ' percent off');
            jump.hidden = false;
          } else {
            jump.hidden = true;
          }
        }

        /* What the next tier is worth, in money.

           Base is the eligible lines /cart.js returned at their own
           pre-discount totals, plus the units being added here at this
           variant's price. Both are this account's contract prices, so the
           figure is the buyer's, not list.

           It is an aggregate, and the function allocates per line, so the two
           can differ by cents. "about" is doing real work in that sentence
           and is not hedging. Suppressed entirely when any cart line went
           unresolved, because a dollar figure cannot degrade gracefully the
           way the unit count can. */
        if (save) {
          var resolved = known && cartState.resolved === true && typeof cartState.cents === 'number';
          if (resolved) {
            var base = cartState.cents + (need + qty) * f.price;
            var delta = Math.floor(base * nextPct / 100) - Math.floor(base * pct / 100);
            if (delta > 0) {
              save.textContent = 'Worth about ' + money(delta) + ' more off this order.';
              save.hidden = false;
            } else {
              save.hidden = true;
            }
          } else {
            save.hidden = true;
          }
        }
      }
      next.hidden = false;
    }

    /* The live row in the table. */
    var rows = document.querySelectorAll('[data-derw-vol-row]');
    for (var r = 0; r < rows.length; r++) {
      var isActive = parseInt(rows[r].getAttribute('data-derw-vol-row'), 10) === mins[idx];
      rows[r].classList.toggle('derw-vol__row--active', isActive);
    }

    /* Project the economics panel, clearly labelled. Patient price never
       changes - it is the shelf price, not a function of order size. */
    var trio = document.querySelector('[data-derw-price]');
    if (trio) {
      var note = trio.querySelector('[data-derw-projected-note]');
      if (!note) {
        note = document.createElement('span');
        note.className = 'derw-price__projected';
        note.setAttribute('data-derw-projected-note', '');
        var yourCell = trio.querySelector('[data-derw-your]');
        if (yourCell && yourCell.parentNode) yourCell.parentNode.appendChild(note);
      }
      if (pct > 0) {
        setText('[data-derw-your]', money(unit));
        if (hasPatient) setText('[data-derw-profit]', money(f.compare - unit));
        trio.classList.add('derw-price--projected');
        note.textContent = 'projected at a ' + projected +
                           (projected === 1 ? ' unit cart' : ' unit cart');
        note.hidden = false;
      } else {
        setText('[data-derw-your]', money(f.price));
        if (hasPatient) setText('[data-derw-profit]', money(f.compare - f.price));
        trio.classList.remove('derw-price--projected');
        note.hidden = true;
      }
    }

    buildTicks(mins, b);

    /* Mark the tick the projection has reached. */
    var ticks = qp.querySelectorAll('[data-derw-qp-tick]');
    for (var s = 0; s < ticks.length; s++) {
      var v = parseInt(ticks[s].getAttribute('data-derw-qp-tick'), 10);
      ticks[s].classList.toggle('is-reached', projected >= v);
      ticks[s].classList.toggle('is-current', v === mins[idx]);
    }

    /* The thumb last, because the domain moves when the cart read lands and
       the position is derived from it. No event is dispatched here, so this
       cannot feed back into the handlers below. */
    var range = qp.querySelector('[data-derw-qp-range]');
    if (range) {
      var wantPos = totalToPos(projected);
      if (parseInt(range.value, 10) !== wantPos) range.value = wantPos;
    }
  }

  function initQP() {
    var qp = qpEl();
    if (!qp || qp.getAttribute('data-derw-qp-wired') === '1') return;
    if (!priceFacts()) return;                 /* logged out, or no price data */
    qp.setAttribute('data-derw-qp-wired', '1');

    var range = qp.querySelector('[data-derw-qp-range]');
    if (range) {
      range.addEventListener('input', function () {
        qpTouched = true;
        setTotal(posToTotal(range.value), true);
        renderQP();
      });
    }

    var input = qtyInput();
    if (input) {
      input.addEventListener('input', function () { qpTouched = true; setQty(input.value, false); renderQP(); });
      input.addEventListener('change', function () { qpTouched = true; setQty(input.value, false); renderQP(); });
    }

    /* The ladder is delegated on its container, which survives a rebuild, so
       a tier-configuration change cannot leave dead rungs behind. */
    var ladder = qp.querySelector('[data-derw-qp-ladder]');
    if (ladder) {
      ladder.addEventListener('click', function (e) {
        var rung = e.target.closest ? e.target.closest('[data-derw-qp-rung]') : null;
        if (!rung || rung.disabled) return;
        var mins = tierMins();
        var i = parseInt(rung.getAttribute('data-derw-qp-rung'), 10);
        if (isNaN(i) || !mins[i]) return;
        qpTouched = true;
        setTotal(mins[i], true);
        renderQP();
      });
    }

    var jump = document.querySelector('[data-derw-qp-jump]');
    if (jump) {
      jump.addEventListener('click', function () {
        var to = parseInt(jump.getAttribute('data-derw-qp-jump-to'), 10);
        if (isNaN(to)) return;
        qpTouched = true;
        setTotal(to, true);
        renderQP();
      });
    }

    /* The theme's own +/- buttons write to the input without firing input, so
       the value is read back on the next frame. */
    document.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-action="increase-picker-quantity"], [data-action="decrease-picker-quantity"]') : null;
      if (!b) return;
      qpTouched = true;
      setTimeout(function () { setQty(currentQty(), false); renderQP(); }, 0);
    }, true);

    setQty(currentQty(), false);
    renderQP();
    readEligibleCart();

    /* Swap the long and short wording when the breakpoint is crossed. */
    if (window.matchMedia) {
      var onBreak = function () { renderQP(); };
      [QP_MQ_NARROW, QP_MQ_TINY].forEach(function (q) {
        var mq = window.matchMedia(q);
        if (mq.addEventListener) mq.addEventListener('change', onBreak);
        else if (mq.addListener) mq.addListener(onBreak);
      });
    }
  }

  /* ======================================================================
     Rx CLINIC ESSENTIALS  (sections/derw-rx-essentials.liquid)
     Added 8 September 2026.

     TWO JOBS, AND NEITHER OF THEM IS PRICING.

     1. Quick Add. The theme's own [data-action="add-to-cart"] handler is
        registered PER SECTION CLASS - theme.js binds it to this.delegateElement
        inside collection, cart, featured-collection and friends - so a brand new
        section type gets no handler at all and the button would do nothing.
        This posts to the same window.routes.cartAddUrl with the same body and
        headers theme.js uses, then dispatches the same bubbling `product:added`
        event. The mini-cart listens for that on document.documentElement
        (theme.js line 6457), so the cart count, the mini-cart re-render and the
        drawer all behave exactly as they do from any other Add to Cart. Nothing
        about the cart is reimplemented here.

     2. The volume nudge. It reuses cartState, readEligibleCart(), tierMins()
        and tierPcts() - the same values that drive the buy-box slider. There is
        no second set of tier numbers and no second cart read in this file.

     WHY THE LINE IS GATED.
     While the SupaEasy function excludes the RX and Medical Supplies tags, Rx
     items do NOT count toward cart volume. Saying they do would promise a
     discount checkout refuses. So a card is called eligible only when its own
     tags miss the published excluded list, and both the note and the nudge stay
     hidden unless at least one card on screen is genuinely eligible. Clear the
     tags from the SupaEasy metafield and the theme setting and this turns itself
     on with no code change.
     ====================================================================== */

  function rxSection() { return document.querySelector('[data-derw-rx-section]'); }

  /* Cards whose tags miss the published excluded list. Same list, same
     normalisation, as readEligibleCart uses for the cart itself. */
  function rxEligibleCards() {
    var sec = rxSection();
    if (!sec) return [];
    var blocked = excludedTags();
    var cards = sec.querySelectorAll('[data-derw-rx-card]');
    var out = [];
    for (var i = 0; i < cards.length; i++) {
      var raw = cards[i].getAttribute('data-derw-rx-tags') || '';
      var tags = raw.split('~');
      var isBlocked = false;
      for (var j = 0; j < tags.length; j++) {
        if (blocked.indexOf(tags[j].trim().toLowerCase()) !== -1) { isBlocked = true; break; }
      }
      if (!isBlocked) out.push(cards[i]);
    }
    return out;
  }

  function renderRx() {
    var sec = rxSection();
    if (!sec) return;
    var note = sec.querySelector('[data-derw-rx-note]');
    var line = sec.querySelector('[data-derw-rx-volume]');
    if (!note && !line) return;               /* logged out: neither is rendered */

    var eligible = rxEligibleCards().length;
    if (note) note.hidden = (eligible === 0);

    if (!line) return;

    /* No eligible card, or the cart could not be read: say nothing rather than
       guess. cartState is null before the first read and false when it failed. */
    if (eligible === 0 || !cartState || cartState === true) { line.hidden = true; return; }

    var mins = tierMins();
    var pcts = tierPcts();
    var qty  = typeof cartState.qty === 'number' ? cartState.qty : 0;
    var idx  = tierIndexFor(qty, mins);

    if (idx >= mins.length - 1) {
      line.textContent = 'You have already unlocked the highest volume tier.';
      line.hidden = false;
      return;
    }

    var need = mins[idx + 1] - qty;
    if (need <= 0) { line.hidden = true; return; }
    line.textContent = 'Add ' + need + ' more ' + (need === 1 ? 'unit' : 'units') +
                       ' to unlock ' + pcts[idx + 1] + '% pricing.';
    line.hidden = false;
  }

  function rxAdd(btn) {
    if (btn.disabled) return;
    var id = btn.getAttribute('data-derw-rx-variant');
    if (!id) return;

    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    var url = (window.routes && window.routes.cartAddUrl) ? window.routes.cartAddUrl : '/cart/add';

    fetch(url + '.js', {
      body: JSON.stringify({ id: id, quantity: 1 }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'   /* Shopify assumes this header */
      }
    }).then(function (res) {
      if (!res.ok) throw new Error('add failed ' + res.status);
      btn.textContent = 'Added';
      btn.classList.add('is-added');

      /* The theme's own signal. It bubbles to document.documentElement where
         the mini-cart increments its count, re-renders and opens the drawer.
         detail.button is what theme.js reads when cartType is "message". */
      btn.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { variant: null, quantity: 1, button: btn }
      }));

      /* Re-read rather than assume: this product may or may not have counted
         toward the eligible total, and only the tag list decides that. */
      cartState = null;
      renderQP();
      renderRx();
      readEligibleCart();

      setTimeout(function () {
        btn.disabled = false;
        btn.textContent = original;
        btn.classList.remove('is-added');
      }, 1600);
    }).catch(function () {
      btn.disabled = false;
      btn.textContent = original;
      if (window.console && console.warn) console.warn('[derw-pdp] Rx quick add failed');
    });
  }

  function initRx() {
    var sec = rxSection();
    if (!sec || sec.hasAttribute('data-derw-rx-bound')) return;
    sec.setAttribute('data-derw-rx-bound', '1');
    sec.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-derw-rx-add]') : null;
      if (!btn) return;
      e.preventDefault();
      rxAdd(btn);
    });
  }

  /* Cart changes: re-read rather than guess. */
  ['cart:updated', 'cart:refresh', 'cart:change', 'ajaxProduct:added'].forEach(function (evt) {
    document.addEventListener(evt, function () { cartState = null; renderQP(); renderRx(); readEligibleCart(); });
  });
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) { cartState = null; readEligibleCart(); }
  });

  /* ==========================================================================
     INFO TOOLTIPS - 8 September 2026

     Two on the PDP, both explaining the same thing: the volume tier is decided
     by TOTAL eligible units across the cart, not by the quantity of one SKU.

     Why any script at all, when :hover and :focus-within would do most of it:

     1. Mobile. A CSS-only tooltip is hover-only, and roughly half these buyers
        are on a phone. Tap has to open it, and a second tap or a tap anywhere
        else has to close it.
     2. Escape has to close it.
     3. The "Projected cart" tip lives in a grid column about 85px wide, and it
        drops to about 130px when the metrics go two-up under 620px. A 300px
        bubble centred on that icon runs off the side of the screen. The script
        measures the bubble against the viewport after showing it and writes a
        --derw-tip-x nudge; the CSS shifts the arrow back by the same amount so
        it still points at the icon.

     It degrades honestly. With JavaScript off the bubble stays hidden and the
     button is inert, which is why the button also carries an aria-label that
     says what the tooltip says it will explain.
     ========================================================================== */

  var TIP_GUTTER = 10;          /* px of clear space kept at the viewport edge */
  var tipsWired = false;

  function tipEls(tip) {
    return {
      btn: tip.querySelector('[data-derw-tip-btn]'),
      bubble: tip.querySelector('[data-derw-tip-bubble]')
    };
  }

  /* Show first, then measure. getBoundingClientRect on a display:none element
     returns zeroes, so the nudge has to be computed after the bubble is in the
     layout, not before. */
  function placeTip(tip) {
    var el = tipEls(tip);
    if (!el.bubble) return;

    el.bubble.style.setProperty('--derw-tip-x', '0px');
    el.bubble.classList.remove('derw-tip__bubble--above');

    var r = el.bubble.getBoundingClientRect();
    var vw = document.documentElement.clientWidth || window.innerWidth;
    var shift = 0;

    if (r.left < TIP_GUTTER) {
      shift = TIP_GUTTER - r.left;
    } else if (r.right > vw - TIP_GUTTER) {
      shift = (vw - TIP_GUTTER) - r.right;
    }
    if (shift) el.bubble.style.setProperty('--derw-tip-x', Math.round(shift) + 'px');

    /* Flip above only when below genuinely does not fit AND above does. A
       bubble half off the bottom of a long page is better than one flipped up
       into the header. */
    var r2 = el.bubble.getBoundingClientRect();
    var vh = document.documentElement.clientHeight || window.innerHeight;
    if (r2.bottom > vh - TIP_GUTTER) {
      var btnRect = el.btn ? el.btn.getBoundingClientRect() : r2;
      if (btnRect.top - r2.height - 9 > TIP_GUTTER) {
        el.bubble.classList.add('derw-tip__bubble--above');
      }
    }
  }

  function closeTip(tip) {
    if (!tip || !tip.classList.contains('is-open')) return;
    var el = tipEls(tip);
    tip.classList.remove('is-open');
    if (el.bubble) el.bubble.hidden = true;
  }

  function closeAllTips(except) {
    var open = document.querySelectorAll('.derw-tip.is-open');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== except) closeTip(open[i]);
    }
  }

  function openTip(tip) {
    var el = tipEls(tip);
    if (!el.bubble) return;
    closeAllTips(tip);
    if (tip.classList.contains('is-open')) { placeTip(tip); return; }
    tip.classList.add('is-open');
    el.bubble.hidden = false;
    placeTip(tip);
  }

  function wireTip(tip) {
    if (tip.getAttribute('data-derw-tip-wired') === '1') return;
    tip.setAttribute('data-derw-tip-wired', '1');

    var el = tipEls(tip);
    if (!el.btn || !el.bubble) return;

    /* A pointerdown on the button toggles. This is what makes tap work, and it
       runs before focus, so the flag below stops the focus handler forcing it
       straight back open on a mouse click that was meant to close it. */
    el.btn.addEventListener('pointerdown', function (e) {
      /* Cleared on a timeout, NOT by the focusin handler. Focus is dispatched
         synchronously inside this event's default action, so focusin always
         sees the flag; the timeout then always clears it. Letting focusin do
         the clearing looked equivalent and was not: a touch tap does not
         always move focus, so the flag could survive the whole interaction and
         silently stop the tooltip opening the next time the buyer TABBED to
         it. Measured at every width - "keyboard focus opens it" failed 10/10
         before this change. */
      tip._derwFromPointer = true;
      setTimeout(function () { tip._derwFromPointer = false; }, 0);
      if (tip.classList.contains('is-open')) {
        closeTip(tip);
      } else {
        openTip(tip);
      }
      /* Keeps a tap from being read as a tap-outside by the document handler. */
      e.stopPropagation();
    });

    /* Some older mobile browsers fire no pointer events. Click is the fallback,
       and it is a no-op when pointerdown already handled this interaction. */
    el.btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (tip._derwFromPointer) return;
      if (tip.classList.contains('is-open')) closeTip(tip); else openTip(tip);
    });

    /* Hover, mouse and stylus only. A touch also fires pointerenter on many
       browsers, which would fight the tap toggle. */
    tip.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'touch') return;
      openTip(tip);
    });

    tip.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'touch') return;
      /* Stays open if the keyboard is still on it. */
      if (el.btn === document.activeElement) return;
      closeTip(tip);
    });

    /* Keyboard. focusin rather than focus so it survives any future markup
       change that puts something focusable inside the bubble. */
    tip.addEventListener('focusin', function () {
      /* A click on the trigger has already toggled it; do not fight that. */
      if (tip._derwFromPointer) return;
      openTip(tip);
    });

    tip.addEventListener('focusout', function (e) {
      tip._derwFromPointer = false;
      if (e.relatedTarget && tip.contains(e.relatedTarget)) return;
      closeTip(tip);
    });
  }

  function initTips() {
    var tips = document.querySelectorAll('[data-derw-tip]');
    for (var i = 0; i < tips.length; i++) wireTip(tips[i]);

    if (tipsWired) return;
    tipsWired = true;

    /* Tap or click anywhere else closes. Capture phase so a handler that stops
       propagation further down cannot leave a tooltip stranded open. */
    document.addEventListener('pointerdown', function (e) {
      if (!document.querySelector('.derw-tip.is-open')) return;
      var inside = e.target && e.target.closest ? e.target.closest('.derw-tip') : null;
      closeAllTips(inside);
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc' && e.keyCode !== 27) return;
      var open = document.querySelector('.derw-tip.is-open');
      if (!open) return;
      var el = tipEls(open);
      closeTip(open);
      /* Focus goes back to the trigger, not to the top of the document. */
      if (el.btn && document.activeElement && open.contains(document.activeElement)) el.btn.focus();
    });

    /* The page moving under an open bubble leaves it pointing at nothing.
       Re-place it rather than closing, so a hover survives a scroll nudge. */
    var pending = false;
    function reflow() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        var open = document.querySelector('.derw-tip.is-open');
        if (open) placeTip(open);
      });
    }
    window.addEventListener('resize', reflow);
    window.addEventListener('scroll', reflow, true);
  }

  function boot() {
    initTips();
    initAccordion();
    syncVariantChip();
    syncFactChips();
    dedupeChips();
    syncPrices();
    initQP();
    renderQP();
    initRx();
    renderRx();
    /* The Rx module needs the eligible-cart total even on a product whose own
       buy box has no slider - every excluded Rx PDP. initQP() returns early
       there, so the first read is kicked off here instead. */
    if (rxSection() && cartState === null) readEligibleCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Rebuilt if the section is re-rendered in the theme editor. */
  document.addEventListener('shopify:section:load', boot);
})();
